import { Box } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import DeliveryForm, { FormValues } from '../../../components/delivery/DeliveryForm';
import PageHeader from '../../../components/PageHeader';
import SelectTypeModal from '../../../components/SelectTypeModal';
import { useGetManagersShortListQuery } from '../../../store/apiSlices/companies/companiesSuppliers/companiesSuppliersApis';
import { useGetSuppliersByManagerIdQuery } from '../../../store/apiSlices/delivery/deliveryApis';
import { useGetExtraCostsOptionsQuery } from '../../../store/apiSlices/extracosts/extraCostsApis';
import { useGetPortLocationsQuery } from '../../../store/apiSlices/ports/portsApis';
import {
  useGetAvailableDatesForRobQuery,
  useGetVesselByManagerIdQuery,
} from '../../../store/apiSlices/robs/robsApis';
import ProductListingContainer from './ProductListingContainer';
import { useSelector } from 'react-redux';
import { isAdmin } from '../../../utils/roles';
import { showErrorToast } from '../../../components/common/BaseToaster';
import { useTranslation } from 'react-i18next';

// Delivery types data
const DELIVERY_TYPES = [
  { value: 'standard', label: 'deliveries.standardLabel' },
  { value: 'adjusting', label: 'deliveries.adjustingLabel' },
  { value: 'secondHand', label: 'deliveries.spPurchaseLabel' },
  { value: 'newBuilding', label: 'deliveries.newBuildingLabel' },
];

// Helper function to map formatted type labels back to raw values
const getRawDeliveryType = (formattedType: string): string => {
  if (!formattedType) return '';

  // First check if it's already a raw value
  const validTypes = ['standard', 'adjusting', 'secondHand', 'newBuilding'];
  const lowerFormatted = formattedType.toLowerCase().trim();
  if (validTypes.includes(lowerFormatted)) {
    return lowerFormatted;
  }

  // Map formatted labels to raw values (handle various formats)
  const typeMap: Record<string, string> = {
    standard: 'standard',
    adjusting: 'adjusting',
    adjustment: 'adjusting', // Handle "Adjustment" from backend
    'second hand': 'secondHand',
    secondhand: 'secondHand',
    '2nd hand vessel acquisition': 'secondHand',
    '2nd hand': 'secondHand',
    acquisition: 'secondHand', // Handle "Acquisition" from backend
    's&p purchase': 'secondHand', // Handle "S&P Purchase" display label
    'new building': 'newBuilding',
    newbuilding: 'newBuilding',
  };

  return typeMap[lowerFormatted] || '';
};

const AddDeliveryContainer: React.FC = () => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: any) => state.user.userInfo);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're in edit mode
  const isEditMode = location.state?.isEdit === true;
  const editData = location.state?.deliveryData;

  const [isDataAvailable, setIsDataAvailable] = useState<boolean>(false);

  // State management
  // In edit mode, use raw deliveryType if available, otherwise map from formatted type
  const getInitialDeliveryType = (): string => {
    if (isEditMode && editData) {
      // Prefer raw deliveryType value
      // if (editData.deliveryType) {
      //   return editData.deliveryType;
      // }
      // Fallback: map formatted type to raw value
      if (editData.type || editData.deliveryType) {
        return getRawDeliveryType(editData.type || editData.deliveryType);
      }
    }
    return '';
  };

  const [currentStep, setCurrentStep] = useState<number>(isEditMode ? 2 : 1); // 1: type selection, 2: form, 3: products
  const [selectedType, setSelectedType] = useState<string>(getInitialDeliveryType());

  const [formData, setFormData] = useState<FormValues>({
    vessel: '',
    manager: null,
    supplier: null,
    port: '',
    pricelist: '',
    date: null,
    remarks: '',
    poNumber: '',
    invoiced: false,
    invoiceNumber: '',
    salesTax: '',
    localTax: '',
    extraCosts: [],
  });

  // React Hook Form lives in container
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    setValue,
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: formData,
  });

  const [showTypeSelectionModal, setShowTypeSelectionModal] = useState<boolean>(false);
  const [typeSelectionError, setTypeSelectionError] = useState<string | null>(null);

  const validateDeliveryType = useCallback(
    (type: string): string | true => {
      const validTypes = ['standard', 'adjusting', 'secondHand', 'newBuilding'];
      if (!type || !validTypes.includes(type)) {
        return t('deliveries.validation.invalidType');
      }
      return true;
    },
    [t],
  );

  // Computed values
  const deliveryTypeLabel = useMemo((): string => {
    const typeMap: Record<string, string> = {
      standard: t('deliveries.standardDelivery'),
      adjusting: t('deliveries.adjustingDelivery'),
      secondHand: t('deliveries.spPurchase'),
      newBuilding: t('deliveries.newBuildingDelivery'),
    };
    return typeMap[selectedType] || '';
  }, [selectedType, t]);

  // Generate add button label based on selected type
  const getAddButtonLabel = useCallback(
    (type: string): string => {
      if (!type) return t('deliveries.genericAddDelivery');
      const deliveryType = DELIVERY_TYPES.find((t) => t.value === type);
      if (!deliveryType) return t('deliveries.genericAddDelivery');
      return t('deliveries.dynamicAddDelivery', { type: t(deliveryType.label) });
    },
    [t],
  );

  // API calls for dropdowns
  const {
    data: portOptions = [],
    isLoading: portsLoading,
    refetch: refetchPorts,
  } = useGetPortLocationsQuery({ search: '' }, { skip: false });

  const {
    data: managerOptions = [],
    isLoading: managersLoading,
    refetch: refetchManagers,
  } = useGetManagersShortListQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const {
    data: extraCostOptions = [],
    isLoading: extraCostsLoading,
    refetch: refetchExtraCosts,
  } = useGetExtraCostsOptionsQuery();

  const managerValue = watch('manager');
  const vesselValue = watch('vessel');

  const numericManagerId = typeof managerValue === 'number' ? managerValue : 0;
  const numericVesselId = vesselValue ? parseInt(String(vesselValue), 10) : 0;

  const { data: vesselOptions = [], isLoading: vesselsLoading } = useGetVesselByManagerIdQuery(
    numericManagerId,
    {
      skip: !numericManagerId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: supplierOptions = [], isLoading: suppliersLoading } =
    useGetSuppliersByManagerIdQuery(numericManagerId, {
      skip: !numericManagerId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    });

  const {
    data: availableDates = { startDate: '', endDate: '', id: 0 },
    refetch: refetchAvailableDates,
  } = useGetAvailableDatesForRobQuery(
    {
      managerId: numericManagerId,
      vesselId: numericVesselId,
    },
    {
      skip: !numericManagerId || !numericVesselId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  // Check for type in URL params and navigation state, and set initial state
  useEffect(() => {
    // Skip type selection modal in edit mode
    if (isEditMode) {
      // Always map through getRawDeliveryType to get the internal value
      const rawType = getRawDeliveryType(editData?.type || editData?.deliveryType || '');
      if (rawType && validateDeliveryType(rawType) === true) {
        setSelectedType(rawType);
        setCurrentStep(3); // Go directly to form
      } else {
        // If type is invalid, show error or default
        setSelectedType('');
        setCurrentStep(2); // Still go to form to show error
      }
      return;
    }

    // First check navigation state (from modal selection)
    const typeFromState = location.state?.deliveryType;
    if (typeFromState && validateDeliveryType(typeFromState) === true) {
      setSelectedType(typeFromState);
      setCurrentStep(2); // Go directly to form
      return;
    }

    // Then check URL parameters
    const typeFromUrl = searchParams.get('type');
    if (typeFromUrl && validateDeliveryType(typeFromUrl) === true) {
      setSelectedType(typeFromUrl);
      setCurrentStep(2); // Go directly to form
    } else {
      // Show type selection modal if no valid type is found
      setShowTypeSelectionModal(true);
    }
  }, [searchParams, location.state, isEditMode, editData, validateDeliveryType]);

  // Reset form when navigating to "Add Delivery" from quick access (not in edit mode)
  useEffect(() => {
    // If we're not in edit mode and the location pathname is /deliveries/add
    // Reset all form data to initial state
    if (!isEditMode && location.pathname === '/deliveries/add') {
      const initialFormData: FormValues = {
        vessel: '',
        manager: null,
        supplier: null,
        port: '',
        pricelist: '',
        date: null,
        remarks: '',
        poNumber: '',
        invoiced: false,
        invoiceNumber: '',
        salesTax: '',
        localTax: '',
        extraCosts: [],
      };

      // Reset form data state
      setFormData(initialFormData);

      // Reset React Hook Form
      reset(initialFormData);

      // Reset selected type if not coming from URL or state
      if (!searchParams.get('type') && !location.state?.deliveryType) {
        setSelectedType('');
        setCurrentStep(1);
      }
    }
  }, [isEditMode, location.pathname, reset, searchParams, location.state]);

  // In edit mode, once manager options are available, populate form and RHF
  useEffect(() => {
    if (!isEditMode || !editData) return;

    const managerOption = managerOptions?.find((m: any) => m?.label === editData?.manager);
    const managerId: number | null =
      typeof managerOption?.value === 'number' ? managerOption.value : null;
    const supplierOption = supplierOptions?.find((s: any) => s?.label === editData?.supplier);
    const supplierId: number | null = Number(supplierOption?.value) || null;
    const portOption = portOptions?.find((p: any) => p?.label === editData?.port);
    const portId: string = portOption?.value ? String(portOption.value) : '';

    // Transform extraCosts from backend format to form format
    // Backend: [{ extraCostId: 1, value: 12.00 }]
    // Form needs: extraCosts: [1] and field "1": "12.00"
    const transformedExtraCosts: any[] = [];
    const extraCostValues: any = {};

    if (Array.isArray(editData?.extraCosts)) {
      editData.extraCosts.forEach((cost: any) => {
        if (cost.extraCostId && cost.value) {
          const costId = String(cost.extraCostId);
          transformedExtraCosts.push(costId);
          // Use the cost ID as the field name (matching the pattern in ProductListingContainer)
          extraCostValues[costId] = String(cost.value);
        }
      });
    }

    const nextValues: FormValues = {
      vessel: editData?.vesselId ? String(editData.vesselId) : '',
      manager: managerId,
      supplier: supplierId,
      port: portId,
      pricelist: editData?.pricelist || '',
      date: editData?.deliveryDate || editData?.date || null,
      remarks: editData?.remarks || '',
      poNumber: editData?.poNumber || null,
      invoiced: editData?.invoiced === 'Yes' ? true : false,
      invoiceNumber: editData?.invoiceNumber || '',
      salesTax: editData?.salesTax || '',
      localTax: editData?.localTax || '',
      extraCosts: transformedExtraCosts,
      ...extraCostValues, // Spread the individual cost values as separate fields
    };

    reset(nextValues);
    setFormData(nextValues);
  }, [isEditMode, editData, managerOptions, reset, supplierOptions, portOptions]);

  useEffect(() => {
    if (managerOptions?.length && !isAdmin(userInfo?.authorityName)) {
      let currentManager = managerOptions.find((m: any) => m.label === userInfo.managerName)?.value;
      if (currentManager) {
        reset({
          manager: currentManager,
        });
      }
    }
  }, [managerOptions, reset, userInfo?.authorityName, userInfo?.managerName]);

  // Handle page reload/close warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Only show alert if user is on the form (step 2) or products (step 3)
      if (currentStep === 2 || currentStep === 3) {
        e.preventDefault();
        // Modern browsers require returnValue to be set
        e.returnValue = '';
        // Some browsers will show this message, others will show a generic message
        return t('common.unsavedChanges');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup the event listener when component unmounts
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentStep, t]);

  // Event handlers
  const handleTypeSelection = useCallback((type: string): void => {
    setSelectedType(type);
    setTypeSelectionError(null); // Clear error on selection
  }, []);

  const handleTypeConfirm = useCallback((): void => {
    // Validate delivery type
    const typeValidation = validateDeliveryType(selectedType);
    if (typeValidation !== true) {
      setTypeSelectionError(typeValidation as string);
      showErrorToast(t('toast.validationError'), typeValidation);
      return;
    }

    setTypeSelectionError(null);
    setShowTypeSelectionModal(false);
    setCurrentStep(2); // Proceed to form
  }, [selectedType, t, validateDeliveryType]);

  const handleTypeModalClose = useCallback((): void => {
    setShowTypeSelectionModal(false);
    navigate('/deliveries');
  }, [navigate]);

  const handleTypeModalCancel = useCallback((): void => {
    setShowTypeSelectionModal(false);
    navigate('/deliveries');
  }, [navigate]);

  // Normalize availableDates to a consistent shape { startDate, endDate }
  const normalizedAvailableDates: {
    startDate: string;
    endDate: string;
    id: number;
  } = useMemo(() => {
    // If API returns an array of date strings, derive min/max
    if (Array.isArray(availableDates)) {
      const datesArray = availableDates.filter(Boolean) as string[];
      if (datesArray.length === 0) return { startDate: '', endDate: '', id: 0 };
      const sorted = [...datesArray].sort();
      return {
        startDate: sorted[0] || '',
        endDate: sorted[sorted.length - 1] || '',
        id: 0,
      };
    }
    // If it's already an object with startDate/endDate, pass through
    if (
      availableDates &&
      typeof availableDates === 'object' &&
      'startDate' in (availableDates as any) &&
      'endDate' in (availableDates as any)
    ) {
      const anyDates = availableDates as any;
      return {
        startDate: anyDates.startDate,
        endDate: anyDates.endDate,
        id: typeof anyDates.id === 'number' ? anyDates.id : 0,
      } as { startDate: string; endDate: string; id: number };
    }
    return { startDate: '', endDate: '', id: 0 };
  }, [availableDates]);

  // Force refetch all APIs when component mounts or when entering form step
  useEffect(() => {
    if (currentStep === 2) {
      refetchPorts();
      refetchManagers();
      refetchExtraCosts();
    }
  }, [currentStep, refetchPorts, refetchManagers, refetchExtraCosts]);

  // Transform API data for form
  const formDropdownOptions = useMemo(() => {
    const managers = managerOptions.map((m) => ({
      value: m.value,
      label: m.label,
    }));
    const vessels = vesselOptions;
    const suppliers = supplierOptions;
    const ports = portOptions;
    const pricelists: any[] = [];
    const extraCostsFromMeta = extraCostOptions;
    const dates = normalizedAvailableDates as {
      startDate: string;
      endDate: string;
    };

    return {
      managers,
      vessels,
      suppliers,
      ports,
      pricelists,
      extraCosts: extraCostsFromMeta,
      dates,
    };
  }, [
    managerOptions,
    vesselOptions,
    supplierOptions,
    portOptions,
    extraCostOptions,
    normalizedAvailableDates,
  ]);

  const formLoadingStates = useMemo(
    () => ({
      managers: managersLoading,
      vessels: vesselsLoading,
      suppliers: suppliersLoading,
      ports: portsLoading,
      extraCosts: extraCostsLoading,
    }),
    [managersLoading, vesselsLoading, suppliersLoading, portsLoading, extraCostsLoading],
  );

  const handleFormSubmit = useCallback((data: FormValues): void => {
    // Store form data and proceed to products section
    setFormData(data);
    setCurrentStep(3);
  }, []);

  const handleBackToForm = useCallback((): void => setCurrentStep(2), []);

  // Always render the container with conditional content
  return (
    <>
      {/* Step 1: Delivery Type Selection Modal */}
      <SelectTypeModal
        title={t('deliveries.selectType')}
        typesLabel={t('deliveries.deliveryTypes')}
        open={showTypeSelectionModal}
        onClose={handleTypeModalClose}
        selectedType={selectedType}
        onChangeType={handleTypeSelection}
        onConfirm={handleTypeConfirm}
        onCancel={handleTypeModalCancel}
        isSubmitting={false}
        typeOptions={DELIVERY_TYPES.map((type) => ({
          ...type,
          label: t(type.label),
        }))}
        confirmButtonLabel={getAddButtonLabel(selectedType)}
        error={typeSelectionError}
      />

      {/* Header - shown for steps 2 and 3 */}
      {(currentStep === 2 || currentStep === 3) && (
        <Box sx={{ mb: 2 }}>
          <Box>
            <PageHeader
              options={[
                {
                  label: t('deliveries.title'),
                  isActive: false,
                  onClick: () => navigate('/deliveries'),
                },
                {
                  label: isEditMode ? t('deliveries.editDelivery') : t('deliveries.addDelivery'),
                  isActive: true,
                  onClick: () => {},
                },
              ]}
            />
          </Box>
        </Box>
      )}

      {/* Step 2: Delivery Form */}
      {currentStep === 2 && (
        <DeliveryForm
          typeLabel={deliveryTypeLabel}
          deliveryType={selectedType}
          onSubmit={handleSubmit(handleFormSubmit)}
          submitting={false}
          dropdownOptions={formDropdownOptions}
          loadingStates={formLoadingStates}
          control={control}
          errors={errors}
          setValues={setValue}
          isAdmin={isAdmin(userInfo.authorityName)}
          isEditMode={isDataAvailable}
        />
      )}

      {/* Step 3: Products Section */}
      {currentStep === 3 && (
        <ProductListingContainer
          deliveryFormData={formData}
          selectedType={selectedType}
          onBack={handleBackToForm}
          isEditMode={isEditMode}
          editData={editData}
          availableDates={normalizedAvailableDates}
          managerOptions={managerOptions}
          vesselOptions={vesselOptions}
          refetchAvailableDates={refetchAvailableDates}
          supplierOptions={supplierOptions}
          portOptions={portOptions}
          extraCostsOptions={extraCostOptions}
          supplierIdForMakerList={formData?.supplier}
          setIsDataAvailable={setIsDataAvailable}
        />
      )}
    </>
  );
};

export default AddDeliveryContainer;
